import naclutil from 'tweetnacl-util';
import RequestBehaviour from '../base/RequestBehaviour';
import Helpers from '../base/Helpers';
import Card from '../models/Card';
import Billing from '../models/Billing';
import FailedEncryptionException from '../exceptions/FailedEncryptionException';

export default class CardTokenization extends RequestBehaviour {
	/**
	 * Card number or PAN
	 */
	number?: string | null = undefined;

	/**
	 * Card security code
	 */
	cvv2?: string | null = undefined;

	/**
	 * Card expire month date (MM)
	 */
	expire_month?: string | null = undefined;

	/**
	 * Card expire year date (YYYY)
	 */
	expire_year?: string | null = undefined;

	/**
	 * Cardholder name
	 */
	cardholder?: string | null = undefined;

	/**
	 * Customer billing address
	 */
	address?: string | null = undefined;

	/**
	 * Customer billing country alpha-2 code (ISO 3166-1)
	 */
	country?: string | null = undefined;

	/**
	 * Customer billing state alpha code (ISO 3166-2)
	 */
	state?: string | null = undefined;

	/**
	 * Customer billing city
	 */
	city?: string | null = undefined;

	/**
	 * Customer billing postal code
	 */
	zip?: string | null = undefined;

	/**
	 * Customer billing phone
	 */
	phone?: string | null = undefined;

	/**
	 * Customer email
	 */
	email?: string | null = undefined;

	/**
	 * Associate and mapping Card model properties to transaction
	 *
	 * @param card
	 */
	setCard(card: Card) {
		this.number = Helpers.cleanString(card.number);
		this.cvv2 = card.cvv2;
		this.cardholder = Helpers.trimValue(card.cardholder);

		if (card.expire_month) {
			this.expire_month = ('00' + card.expire_month).slice(-2);
		}

		if (card.expire_year) {
			this.expire_year = String(card.expire_year);
		}
	}

	/**
	 * Associate and mapping Billing model properties to transaction
	 *
	 * @param billing
	 */
	setBilling(billing: Billing) {
		this.address = Helpers.trimValue(billing.address);
		this.country = billing.country;
		this.state = billing.state;
		this.city = Helpers.trimValue(billing.city);
		this.zip = billing.zip;
		this.phone = billing.phone;
	}

	/**
	 * Check if current request needs encryption.
	 *
	 * @return
	 */
	isEncryptable(): boolean {
		return Boolean(this.number || this.cvv2 || this.expire_month || this.expire_year || this.cardholder || this.email);
	}

	/**
	 * Encrypt fields with sensitive data.
	 *
	 * @param publicKey
	 * @return
	 * @throws FailedEncryptionException
	 */
	withEncryption(publicKey?: string | null): string {
		if (!publicKey) {
			throw new FailedEncryptionException('Could not process transaction without merchant public key.');
		}

		try {
			const keyPair = this.sdk_key_pair ?? Helpers.getKeyPair();

			if (this.number) this.number = Helpers.encrypt(this.number, publicKey, keyPair);
			if (this.cvv2) this.cvv2 = Helpers.encrypt(this.cvv2, publicKey, keyPair);
			if (this.expire_month) this.expire_month = Helpers.encrypt(this.expire_month, publicKey, keyPair);
			if (this.expire_year) this.expire_year = Helpers.encrypt(this.expire_year, publicKey, keyPair);
			if (this.email) this.email = Helpers.encrypt(this.email, publicKey, keyPair);
			if (this.cardholder) this.cardholder = Helpers.encrypt(this.cardholder, publicKey, keyPair);

			const sdk_public_key = naclutil.encodeBase64(keyPair.publicKey);

			this.sdk_key_pair = keyPair;

			return sdk_public_key;
		} catch (error) {
			throw new FailedEncryptionException('Encryption process encountered an unexpected error.');
		}
	}
}
